import React, { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

interface OidcRecoveryClient {
  signinSilent: () => Promise<unknown>;
  signinRedirect: () => Promise<unknown>;
}

interface RecoveryLatch {
  current: boolean;
}

/**
 * Recover one expired OIDC session at a time. Silent renewal stays the first
 * choice; an interactive redirect is used only when the refresh grant fails.
 */
export async function recoverOidcSession(
  auth: OidcRecoveryClient,
  recovering: RecoveryLatch,
): Promise<void> {
  if (recovering.current) return;
  recovering.current = true;
  try {
    await auth.signinSilent();
  } catch {
    try {
      await auth.signinRedirect();
    } catch {
      // The OIDC provider owns user-facing navigation errors. Keep this
      // bridge side-effect-free so it cannot create a retry/redirect loop.
    }
  } finally {
    recovering.current = false;
  }
}

/**
 * Bridges the ZewstID auth state into the axios `api` instance AND the
 * legacy Zustand `authStore`. Renders no UI; mounted once inside the
 * AuthProvider so it can observe access-token changes (initial sign-in,
 * silent renew, sign-out) and keep both downstream consumers in sync
 * without forcing every caller to grab the token via the OIDC hook.
 *
 * Why the authStore mirror: `services/socket.ts` constructs its socket.io
 * client with `auth: { token: useAuthStore.getState().token }` — the
 * legacy code path from before the ZewstID cutover. Without this mirror
 * it always reads `null`, the WS handshake's `auth` payload is
 * `{ token: null }`, and ws-gateway's `io.use()` middleware rejects with
 * "Authentication required" → reconnect loop + "Offline" indicator.
 *
 * The right long-term fix is to migrate socket.ts (and any other reader
 * of authStore.token) onto `useAuth()` directly so there's a single
 * source of truth. This mirror unblocks the deployed IDE today without
 * touching socket.ts's reconnection semantics.
 *
 * The axios interceptor in `services/api.ts` stays responsible for
 * handling 401s and surfacing toasts.
 */
const ApiBridge: React.FC = () => {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const setToken = useAuthStore((s) => s.setToken);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    if (accessToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      setToken(accessToken);
    } else if (!auth.isLoading) {
      delete api.defaults.headers.common['Authorization'];
      logout();
    }
  }, [accessToken, auth.isLoading, setToken, logout]);

  // Auto-recover instead of the "logged-in but tokenless" limbo.
  //
  // ZewstID's access token lives ~5 min; `automaticSilentRenew` is supposed to
  // refresh it via the offline_access refresh token. If that silent renew ever
  // fails — or the access token lapses without one — nothing here used to
  // recover: the app kept a stale/empty token, and anything that needs the
  // access token in-band (the terminal WS, `wss://…/pty/…?token=<access_token>`)
  // sat forever on the boot screen with no `/pty` ever sent.
  //
  // Now, on either failure we try ONE silent retry (refresh grant, no
  // navigation — invisible when it works), and only fall back to an interactive
  // re-auth if that fails (transparent while the ZewstID SSO cookie is alive;
  // a real login prompt only if the SSO session is truly gone). A ref guards
  // against overlapping recoveries. This is a safety net — when silent renew is
  // healthy it never fires.
  const recoveringRef = useRef(false);
  useEffect(() => {
    const events = auth.events;
    if (!events) return undefined;
    const recover = () => {
      void recoverOidcSession(auth, recoveringRef);
    };
    events.addSilentRenewError(recover);
    events.addAccessTokenExpired(recover);
    return () => {
      events.removeSilentRenewError(recover);
      events.removeAccessTokenExpired(recover);
    };
  }, [auth]);

  return null;
};

export default ApiBridge;
